/**
 * Constantes visuales del PRODUCTO que TypeScript necesita conocer.
 *
 * Aqui ya NO hay colores. Los colores viven en dos hojas:
 *
 * - `estilos/paleta.css`: nivel 1, valores crudos en OKLCH. Ningun componente
 *   la lee.
 * - `estilos/semantico.css`: roles (--bg-*, --text-*, --status-*,
 *   --severity-*...). Es lo unico que leen los componentes, como
 *   `'var(--text-secondary)'` en `sx`.
 *
 * El acento sale de `Organizacion.marca.colorPrimario` en tiempo de ejecucion
 * (`estilos/acento.ts`). Lo que queda en este archivo son medidas que tienen
 * que cuadrar al pixel entre varios archivos y atajos con nombre a los roles
 * que mas se repiten. Ningun valor identifica a un cliente. Ver
 * `backend/tests/sinMarca.test.ts`.
 */

/**
 * Gravedad colposcopica. Fija, del producto, NUNCA de la marca.
 *
 * Tres razones para que estos colores no salgan de `Organizacion.marca`:
 *
 * 1. Un consultorio cuyo color sea un rosa suave haria que «cambios mayores»
 *    —el hallazgo que cambia la conducta— se viera amable. Y uno con marca
 *    roja haria que todo pareciera urgente. La gravedad no puede depender de
 *    quien pago el logo.
 * 2. Los informes se imprimen, muchas veces en blanco y negro. Por eso el
 *    grado se codifica ademas con RELLENO y CONTEO de celdas: el tono es
 *    refuerzo, no el portador del significado. Ver `EscalaDeGrado`.
 * 3. Quien no distingue rojo de verde tiene que poder leer la escala. El
 *    conteo de celdas se lo da.
 *
 * Los tonos de menores e intermedios salen de la colposcopia misma —el
 * epitelio acetoblanco y la zona que no capta yodo—; «mayores» va en la
 * familia ladrillo, porque lo que mide riesgo es rojo. Cada grado tiene
 * variante oscura en `semantico.css`; aqui solo se nombran los roles.
 */
export const GRAVEDAD = {
  /** Relleno de celda. Menores es palido a proposito: necesita borde. */
  menores: 'var(--severity-menores)',
  intermedios: 'var(--severity-intermedios)',
  mayores: 'var(--severity-mayores)',
} as const;

export type ClaveGravedad = keyof typeof GRAVEDAD;

/** Texto de cada grado sobre superficie o sobre su lavado. Cumple AA. */
export const GRAVEDAD_TEXTO: Record<ClaveGravedad, string> = {
  menores: 'var(--severity-menores-text)',
  intermedios: 'var(--severity-intermedios-text)',
  mayores: 'var(--severity-mayores-text)',
};

/** Fondo lavado de cada grado (insignias, filas marcadas). */
export const GRAVEDAD_FONDO: Record<ClaveGravedad, string> = {
  menores: 'var(--severity-menores-wash)',
  intermedios: 'var(--severity-intermedios-wash)',
  mayores: 'var(--severity-mayores-wash)',
};

/** Borde de cada grado (contorno de insignias y celdas). */
export const GRAVEDAD_BORDE: Record<ClaveGravedad, string> = {
  menores: 'var(--severity-menores-border)',
  intermedios: 'var(--severity-intermedios-border)',
  mayores: 'var(--severity-mayores-border)',
};

/**
 * Un rol mezclado con transparente, en porcentaje. Sustituye a `alpha()` de
 * MUI, que no acepta `var(--x)`: `mezclar(GRAVEDAD.mayores, 6)`.
 */
export function mezclar(rol: string, porcentaje: number): string {
  return `color-mix(in oklch, ${rol} ${porcentaje}%, transparent)`;
}

/**
 * Medidas de la cascara. Viven aqui porque la barra lateral, la cinta de
 * paciente y el desplazamiento pegajoso tienen que cuadrar al pixel entre
 * varios archivos.
 */
export const CHASIS = {
  /** Barra lateral de Pulso (--sidebar-w). */
  rielAncho: 248,
  /** Barra superior de Pulso (--topbar-h). */
  cabeceraAlto: 68,
  cintaAlto: 56,
  /** Ancho util para formularios: mas alla de esto una linea de texto cansa. */
  anchoFormulario: 1120,
  /** Ancho util para tablas: se aprovecha la pantalla completa. */
  anchoTabla: 1560,
} as const;

/** Ancho del canal de folios. Cabe `C-00001` en monoespaciada sin apretarse. */
export const CANAL_FOLIO = 78;
